import { useModal } from "@ebay/nice-modal-react";
import { Base64 } from "js-base64";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { useCheckFileMaxSize } from "components/Environment/useCheckFileMaxSize";
import FlashContext from "components/FlashMessages/FlashContext";
import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerForm from "ds/components/DrawerNew/Form";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import useTypedContext from "hooks/useTypedContext";
import { ConfigElement, ConfigInput, ConfigType } from "types/generated";
import { getDrawerFormFix } from "utils/css";
import { fileToBase64 } from "utils/file";
import Banner from "ds/components/Banner";

import FormDescriptionField from "../FormItems/DescriptionField";
import FormFileUploadEditorTabs, {
  FileUploadEditorTabsValues,
} from "../FormItems/FileUploadEditorField";
import FormIsSecretField from "../FormItems/IsSecretField";
import FormPathField from "../FormItems/PathField";

type MountedFilesFormValues = {
  id: string;
  description: string;
  fileBody: string;
  file: File | null;
  writeOnly: boolean;
};

type DrawerMountedFileProps = {
  file?: ConfigElement;
  onSubmit: (data: ConfigInput) => Promise<string | undefined>;
};

const mountedFilesFormDrawer = createDrawer(({ file, onSubmit }: DrawerMountedFileProps) => {
  const isEdit = !!file;
  const drawer = useModal();
  const [loading, setLoading] = useState(false);
  const { reportSuccess } = useTypedContext(FlashContext);
  const { fileContent, exceedsFileSize, isFileBroken } = useCheckFileMaxSize(file?.value ?? "");

  const builderForm = useForm<MountedFilesFormValues>({
    defaultValues: {
      id: file?.id || "",
      description: file?.description || "",
      fileBody: fileContent,
      writeOnly: file?.writeOnly ?? false,
    },
    mode: "onChange",
  });

  const {
    handleSubmit,
    formState: { isDirty },
    setValue,
  } = builderForm;

  useEffect(() => {
    if (!isFileBroken) {
      setValue("fileBody", fileContent);
    }
  }, [fileContent, isFileBroken, setValue]);

  const closeDrawer = () => {
    drawer.resolve();
    drawer.hide();
  };
  const initialTab = isEdit
    ? FileUploadEditorTabsValues.AddManually
    : FileUploadEditorTabsValues.Upload;

  const submitHandler = async (formData: MountedFilesFormValues) => {
    try {
      setLoading(true);

      const base64 = formData.file && (await fileToBase64(formData.file));
      const base64File = base64 ? base64.toString().split(",")[1] : "";
      const fileValue = formData.file ? base64File : Base64.encode(formData.fileBody);

      const fileData: ConfigInput = {
        id: formData.id,
        description: formData.description || "",
        writeOnly: formData.writeOnly,
        value: fileValue,
        type: ConfigType.FileMount,
        fileMode: null,
      };

      const id = await onSubmit(fileData);

      if (id) {
        reportSuccess({
          message: `File ${id} successfully ${isEdit ? "updated" : "added"}`,
        });
        closeDrawer();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <DrawerForm isDirty={isDirty}>
      <FormProvider {...builderForm}>
        <form onSubmit={handleSubmit(submitHandler)} {...getDrawerFormFix()}>
          <DrawerHeader justify="between">
            <DrawerHeaderTitle title={isEdit ? "Edit file" : "Add file"} />
            <DrawerCloseIcon />
          </DrawerHeader>
          <DrawerBody gap="x-large" fullHeight>
            {isFileBroken ? (
              <Banner variant="danger" title="We couldn't load the file.">
                Please try again later
              </Banner>
            ) : (
              <>
                <FormPathField name="id" disabled={isEdit} />
                <FormIsSecretField name="writeOnly" />
                <FormDescriptionField name="description" />

                <FormFileUploadEditorTabs
                  uploadName="file"
                  contentName="fileBody"
                  fullHeight
                  initialTab={initialTab}
                  exceedsFileSize={exceedsFileSize}
                />
              </>
            )}
          </DrawerBody>
          <DrawerFooter>
            <DrawerFooterActions>
              <DrawerCancelButton />
              <Button variant="primary" type="submit" disabled={loading || isFileBroken}>
                {isEdit ? "Save" : "Create"}
              </Button>
            </DrawerFooterActions>
          </DrawerFooter>
        </form>
      </FormProvider>
    </DrawerForm>
  );
});

export const showMountedFilesFormDrawer = createDrawerTrigger(mountedFilesFormDrawer);
